import * as React from 'react';
import { useDispatch } from 'react-redux';
import { FeatureItem, FixRequest, FixRequestStatusType } from '../../types';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    EyeInvisibleOutlined,
    InfoCircleOutlined,
    ToolOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    EditOutlined,
    ClockCircleFilled,
    EllipsisOutlined,
    LoadingOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { globalMessage } from '../../utils/globalMessage';
import UpdateFixDetailsModal from './UpdateFixDetailsModal';
import Dropdown from 'antd/lib/dropdown';
import { MenuProps } from 'antd/lib/menu';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { IgnoreUnignoreFRItemRequestItem, tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

export const getFeatureItemActions = (
    requiresFixRequestsApprovals: boolean,
    isItemIgnored: boolean,
    canAddFixRequestForFeatureItem: boolean,
    isReleaseKickedOff: boolean,
    fixRequest?: FixRequest,
) => {
    if (!fixRequest) {
        const items: MenuProps['items'] = [
            {
                label: requiresFixRequestsApprovals ? 'Request approval for a fix' : 'Pull in as fix...',
                key: 'addFix',
                icon: <ToolOutlined style={{ color: antGrey.grey7 }} />,
                disabled: !canAddFixRequestForFeatureItem || !isReleaseKickedOff,
            },
            {
                label: isItemIgnored ? 'Include' : 'Ignore',
                key: 'includeIgnore',
                icon:
                    isItemIgnored ?
                        <EyeOutlined style={{ color: antGrey.grey7 }} />
                    :   <EyeInvisibleOutlined style={{ color: antGrey.grey7 }} />,
            },
        ];

        return items;
    } else {
        if (requiresFixRequestsApprovals) {
            const items: MenuProps['items'] = [
                {
                    label: 'Approve this fix',
                    key: 'approveFix',
                    icon: <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />,
                    disabled: fixRequest.status?.status === FixRequestStatusType.FixRequestApproved,
                },
                {
                    label: 'Reject this fix',
                    key: 'rejectFix',
                    icon: <CloseCircleFilled style={{ color: runwayColors.statusRed }} />,
                    disabled: fixRequest.status?.status === FixRequestStatusType.FixRequestRejected,
                },
                {
                    label: 'Move this fix to pending approval',
                    key: 'moveFixToPending',
                    icon: <ClockCircleFilled style={{ color: runwayColors.statusYellow }} />,
                    disabled: fixRequest.status?.status === FixRequestStatusType.FixRequestPending,
                },
                {
                    label: 'Edit fix details',
                    key: 'editFixDetails',
                    icon: <EditOutlined style={{ color: antGrey.grey7 }} />,
                },
                {
                    label: isItemIgnored ? 'Include' : 'Ignore',
                    key: 'includeIgnore',
                    icon:
                        isItemIgnored ?
                            <EyeOutlined style={{ color: antGrey.grey7 }} />
                        :   <EyeInvisibleOutlined style={{ color: antGrey.grey7 }} />,
                },
            ];

            return items;
        } else {
            const items: MenuProps['items'] = [
                {
                    label: 'Edit fix details',
                    key: 'editFixDetails',
                    icon: <EditOutlined style={{ color: antGrey.grey7 }} />,
                },
                {
                    label: isItemIgnored ? 'Include' : 'Ignore',
                    key: 'includeIgnore',
                    icon: <InfoCircleOutlined style={{ color: runwayColors.info }} />,
                },
            ];
            return items;
        }
    }
};

interface FeatureItemActionsProps extends FeatureItem, ReleaseComponentProps {
    workingBranchEnabled: boolean;
    requiresFixRequestsApprovals: boolean;
    canAddFixRequestForFeatureItem: boolean;
    onClickAddFixRequest?: (workItemId: string) => void;
    isReleaseKickedOff: boolean;
    stepRunId: string;
}

const FeatureItemActions: React.FC<FeatureItemActionsProps> = (props: FeatureItemActionsProps) => {
    const dispatch = useDispatch();

    const featureItem: FeatureItem = {
        id: props.id,
        ticket: props.ticket,
        code: props.code,
        explanationText: props.explanationText,
        ignored: props.ignored,
        autoIgnored: props.autoIgnored,
        isItemDone: props.isItemDone,
        isInLatestOrSelectedStagingBuild: props.isInLatestOrSelectedStagingBuild,
        isInLatestWorkingBranchBuild: props.isInLatestWorkingBranchBuild,
        debugVariant: props.debugVariant,
        associatedStabilityIssues: props.associatedStabilityIssues,
        fixRequest: props.fixRequest,
    };

    const ignoreIsLoading = useIsLoading(ReleaseStepUpdatable.IgnoreFRItem, props.id);
    const ignoreError = useError(ReleaseStepUpdatable.IgnoreFRItem, props.id);

    const unignoreIsLoading = useIsLoading(ReleaseStepUpdatable.UnignoreFRItem, props.id);
    const unignoreError = useError(ReleaseStepUpdatable.UnignoreFRItem, props.id);

    const approveFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);
    const approveFixRequestSuccess = useUpdated(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);
    const approveFixRequestError = useError(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);

    const rejectFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);
    const rejectFixRequestSuccess = useUpdated(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);
    const rejectFixRequestError = useError(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);

    const moveFixRequestToPendingApprovalIsLoading = useIsLoading(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const moveFixRequestToPendingApprovalSuccess = useUpdated(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const moveFixRequestToPendingApprovalError = useError(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );

    const [isFeatureItemActionLoading, setIsFeatureItemActionLoading] = React.useState(false);
    const [showEditFixDetailsModal, setShowEditFixDetailsModal] = React.useState(false);

    const isIgnored = props.ignored;

    const tryIgnoreUnignoreItem = (ignore: boolean) => {
        const type = ignore ? ReleaseStepUpdatable.IgnoreFRItem : ReleaseStepUpdatable.UnignoreFRItem;

        const items: IgnoreUnignoreFRItemRequestItem[] =
            props.groupedCodeItems ?
                props.groupedCodeItems.map((item) => ({
                    frItemId: item.id,
                    isIgnore: ignore,
                    isAutoIgnoreItem: item.autoIgnored,
                }))
            :   [{ frItemId: props.id, isIgnore: ignore, isAutoIgnoreItem: props.autoIgnored }];

        dispatch(
            tryUpdateReleaseStep(
                type,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    items: items,
                },
                props.id,
            ),
        );
    };

    const tryApproveFixRequest = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.ApproveFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    const tryRejectFixRequest = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.RejectFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    const tryMoveFixRequestToPendingApproval = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    React.useEffect(() => {
        if (
            ignoreIsLoading ||
            unignoreIsLoading ||
            approveFixRequestIsLoading ||
            rejectFixRequestIsLoading ||
            moveFixRequestToPendingApprovalIsLoading
        ) {
            setIsFeatureItemActionLoading(true);
        } else {
            setIsFeatureItemActionLoading(false);
        }
    }, [
        ignoreIsLoading,
        unignoreIsLoading,
        approveFixRequestIsLoading,
        rejectFixRequestIsLoading,
        moveFixRequestToPendingApprovalIsLoading,
        isFeatureItemActionLoading,
    ]);

    React.useEffect(() => {
        if (unignoreError) {
            globalMessage.error('Item include failed to update. Please try again');
        }
    }, [unignoreError]);

    React.useEffect(() => {
        if (unignoreError) {
            globalMessage.error('Item include failed to update. Please try again');
        }
    }, [unignoreError]);

    React.useEffect(() => {
        if (ignoreError) {
            globalMessage.error('Item ignore failed to update. Please try again');
        }
    }, [ignoreError]);

    React.useEffect(() => {
        if (approveFixRequestSuccess) {
            globalMessage.success('Fix Request approved successfully!');
        }
    }, [approveFixRequestSuccess]);

    React.useEffect(() => {
        if (approveFixRequestError) {
            globalMessage.error(approveFixRequestError);
        }
    }, [approveFixRequestError]);

    React.useEffect(() => {
        if (rejectFixRequestSuccess) {
            globalMessage.success('Fix Request rejected successfully!');
        }
    }, [rejectFixRequestSuccess]);

    React.useEffect(() => {
        if (rejectFixRequestError) {
            globalMessage.error(rejectFixRequestError);
        }
    }, [rejectFixRequestError]);

    React.useEffect(() => {
        if (moveFixRequestToPendingApprovalSuccess) {
            globalMessage.success('Fix Request moved to pending approval successfully!');
        }
    }, [moveFixRequestToPendingApprovalSuccess]);

    React.useEffect(() => {
        if (moveFixRequestToPendingApprovalError) {
            globalMessage.error(moveFixRequestToPendingApprovalError);
        }
    }, [moveFixRequestToPendingApprovalError]);

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        switch (e.key) {
            case 'addFix': {
                props.onClickAddFixRequest(props.id);
                break;
            }
            case 'includeIgnore': {
                tryIgnoreUnignoreItem(!isIgnored);
                break;
            }
            case 'approveFix': {
                tryApproveFixRequest();
                break;
            }
            case 'rejectFix': {
                tryRejectFixRequest();
                break;
            }
            case 'moveFixToPending': {
                tryMoveFixRequestToPendingApproval();
                break;
            }
            case 'editFixDetails': {
                setShowEditFixDetailsModal(true);
                break;
            }
            default:
                break;
        }
    };

    const items = getFeatureItemActions(
        props.requiresFixRequestsApprovals,
        isIgnored,
        props.canAddFixRequestForFeatureItem,
        props.isReleaseKickedOff,
        props.fixRequest,
    );

    const menuProps = {
        items,
        onClick: handleMenuClick,
    };

    const isLoading =
        isFeatureItemActionLoading ||
        ignoreIsLoading ||
        approveFixRequestIsLoading ||
        rejectFixRequestIsLoading ||
        moveFixRequestToPendingApprovalIsLoading;

    return (
        <div>
            {showEditFixDetailsModal && props.fixRequest && (
                <UpdateFixDetailsModal
                    orgId={props.orgId}
                    appId={props.appId}
                    stepRunId={props.stepRunId}
                    releaseId={props.releaseId}
                    onCancel={() => setShowEditFixDetailsModal(false)}
                    fixRequest={props.fixRequest}
                    featureItemId={featureItem.id}
                />
            )}
            <Dropdown.Button
                menu={menuProps}
                type="link"
                buttonsRender={([_, rightButton]) => [null, rightButton]}
                icon={
                    isLoading ?
                        <LoadingOutlined style={{ color: antGrey.grey7 }} />
                    :   <EllipsisOutlined style={{ fontSize: '28px', color: antGrey.grey7 }} />
                }
                trigger={['click']}
            ></Dropdown.Button>
        </div>
    );
};

export default FeatureItemActions;
